import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  CircularProgress
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";
import { colors, fonts, spacing, borderRadius } from "../../constants";
import {
  normalizeLifecycleStatus,
  normalizeId
} from "../../utils/queryListUtils";

/**
 * QueryTableActions Component
 * Action buttons for query row
 */
const QueryTableActions = ({
  query,
  onView,
  onDelete,
  onReopen,
  onClose,
  reopeningId,
  closingId
}) => {
  let currentUserId = null;
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const currentUser = JSON.parse(userStr);
      currentUserId = currentUser?.id || currentUser?._id || null;
    }
  } catch (error) {
    currentUserId = null;
  }

  const raisedById = normalizeId(query?.raisedBy);
  const isRaiser = Boolean(
    currentUserId &&
    raisedById &&
    currentUserId.toString() === raisedById.toString()
  );
  const isClosed =
    normalizeLifecycleStatus(query?.statusLabel || query?.status) === "closed";
  const [accessRights, setAccessRights] = useState({});

  useEffect(() => {
    try {
      const rights = JSON.parse(localStorage.getItem("AccessRights")) || {};
      setAccessRights(rights);
    } catch {
      setAccessRights({});
    }
  }, []);
  const shouldShowEdit = accessRights["1041"] === "Y";
  const shouldShowDelete = accessRights["1044"] === "Y";
  const shouldShowClose = accessRights["1045"] === "Y";
  const shouldShowNeed = accessRights["1046"] === "Y";
  return (
    <Box
      sx={{
        display: "flex",
        gap: spacing.xs,
        alignItems: "center",
        justifyContent: "flex-end"
      }}
    >
      {isClosed && isRaiser && (
        <Tooltip title="Need Clarification">
          <span>
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onReopen(query);
              }}
              disabled={reopeningId === query._id}
              sx={{
                textTransform: "none",
                fontSize: fonts.fontSizeXs,
                fontWeight: fonts.weightSemiBold,
                borderRadius: borderRadius.button,
                padding: "4px 12px",
                minWidth: "auto",
                whiteSpace: "nowrap",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: colors.textWhite,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)"
                },
                "&:disabled": {
                  backgroundColor: colors.textDisabled,
                  color: colors.textSecondary
                }
              }}
            >
              {reopeningId === query._id ? (
                <CircularProgress size={16} sx={{ color: colors.textWhite }} />
              ) : (
                "Need Clarification"
              )}
            </Button>
          </span>
        </Tooltip>
      )}
      {!isClosed && onClose && (
        <Tooltip title="Close query">
          <span>
            {shouldShowClose && (
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose(query);
                }}
                disabled={closingId === query._id}
                sx={{
                  textTransform: "none",
                  fontSize: fonts.fontSizeXs,
                  fontWeight: fonts.weightSemiBold,
                  borderRadius: borderRadius.button,
                  padding: "4px 12px",
                  minWidth: "auto",
                  background:
                    "linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)",
                  color: colors.textWhite,
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)"
                  },
                  "&:disabled": {
                    backgroundColor: colors.textDisabled,
                    color: colors.textSecondary
                  }
                }}
              >
                {closingId === query._id ? (
                  <CircularProgress
                    size={16}
                    sx={{ color: colors.textWhite }}
                  />
                ) : (
                  "Close"
                )}
              </Button>
            )}
          </span>
        </Tooltip>
      )}
      <Tooltip title="View / Edit">
        {shouldShowEdit && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onView(query);
            }}
            sx={{
              color: colors.primaryDark,
              "&:hover": { backgroundColor: `${colors.primaryTint}` }
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        )}
      </Tooltip>
      <Tooltip title="Delete">
        {shouldShowDelete && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(query);
            }}
            sx={{
              color: colors.error,
              "&:hover": { backgroundColor: colors.error + "20" }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Tooltip>
    </Box>
  );
};

export default QueryTableActions;
